import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { ParkingSpotReadModel } from '@client/api';
import {
  getBuildingId,
  isDefinedNumber,
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toNumberString,
} from '@client/toolkit';

import { PortfolioPaths } from '../../../../../portfolio.router';

interface BuildingParkingSpotsTableRowProps {
  parkingSpot?: ParkingSpotReadModel;
  isFetching?: boolean;
}

export function BuildingParkingSpotsTableRow({ parkingSpot, isFetching }: BuildingParkingSpotsTableRowProps) {
  const { t: tType } = useTranslation('type');

  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (!parkingSpot?.id) return;

    const buildingId = getBuildingId(parkingSpot.path);

    navigate(
      generatePath(PortfolioPaths.Space, {
        buildingId: buildingId,
        spaceId: parkingSpot.id,
        discriminator: parkingSpot.discriminator,
      }),
      { replace: true },
    );
  }, [parkingSpot?.path, parkingSpot?.id, parkingSpot?.discriminator, navigate]);

  return (
    <TableRow hover={!!parkingSpot?.id} onClick={handleClick}>
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(parkingSpot?.name)}>
          <strong className="whitespace-nowrap">{parkingSpot?.name}</strong>
        </Text>
        <Text
          as="div"
          className="whitespace-nowrap text-secondary"
          isEmpty={!isDefinedString(parkingSpot?.parent?.name)}
        >
          {parkingSpot?.parent?.name}
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(parkingSpot?.name)}>{tType(`ParkingSpotType.${parkingSpot?.type}`)}</Text>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedNumber(parkingSpot?.area?.metric)}>
          {toNumberString(parkingSpot?.area?.metric ?? undefined, undefined, {
            unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
          })}
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(parkingSpot?.description)}>{parkingSpot?.description}</Text>
      </TableCell>
    </TableRow>
  );
}
